import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CloseIcon from "@mui/icons-material/Close";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt } from "../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";

interface CompleteWorkDialogProps {
  open: boolean;
  offerNumber: string;
  onComplete: (data: {
    completionSummary: string;
    providerNotes?: string;
    serviceReportFiles?: File[];
  }) => void;
  onClose: () => void;
  loading?: boolean;
}

/* -- Blue accent tokens (matching ConfirmServiceDialog pattern but blue-themed) -- */
const blue = "#3B82F6";
const blueDark = "#2563EB";
const blueSoft = "rgba(59, 130, 246, 0.06)";

const ACCEPTED_FILE_TYPES = ".pdf,.jpg,.jpeg,.png,.doc,.docx";
const MAX_FILE_SIZE_MB = 20;

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const CompleteWorkDialog: React.FC<CompleteWorkDialogProps> = ({
  open,
  offerNumber,
  onComplete,
  onClose,
  loading = false,
}) => {
  useRedesignSetup();
  const { translate } = useTranslation(["serviceOffers", "common"]);
  const isPhone = useMediaQuery("(max-width:600px)");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [completionSummary, setCompletionSummary] = useState("");
  const [summaryError, setSummaryError] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState("");

  // Inject dialog-specific keyframes
  useEffect(() => {
    const styleId = "complete-work-dialog-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes completeDialogSlideUp {
          0% { opacity: 0; transform: translateY(24px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes completePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(59,130,246,0.15); }
          50% { box-shadow: 0 0 0 8px rgba(59,130,246,0); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setCompletionSummary("");
      setSummaryError(false);
      setSelectedFiles([]);
      setFileError("");
    }
  }, [open]);

  const handleComplete = () => {
    if (!completionSummary.trim()) {
      setSummaryError(true);
      return;
    }
    setSummaryError(false);

    onComplete({
      completionSummary: completionSummary.trim(),
      serviceReportFiles: selectedFiles.length > 0 ? selectedFiles : undefined,
    });
  };

  const handleClose = () => {
    if (loading) return;
    setCompletionSummary("");
    setSummaryError(false);
    setSelectedFiles([]);
    setFileError("");
    onClose();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setFileError("");

    const newFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      // Size check
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setFileError(translate("completeWork.fileTooLarge", { max: MAX_FILE_SIZE_MB }));
        continue;
      }
      // Avoid duplicate names
      if (!selectedFiles.some(f => f.name === file.name) && !newFiles.some(f => f.name === file.name)) {
        newFiles.push(file);
      }
    }

    if (newFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...newFiles]);
    }
    // Reset the input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveFile = (fileName: string) => {
    setSelectedFiles(prev => prev.filter(f => f.name !== fileName));
    setFileError("");
  };

  const isValid = completionSummary.trim().length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: dt.radiusLg,
          overflow: "hidden",
          fontFamily: dt.font,
          "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
          animation: "completeDialogSlideUp 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          ...(isPhone && {
            margin: "16px",
            width: "calc(100% - 32px)",
            maxWidth: "none",
          }),
        },
      }}
    >
      {/* -- Header gradient accent bar -- */}
      <Box
        sx={{
          height: "4px",
          background: `linear-gradient(90deg, ${blue} 0%, #60A5FA 50%, ${blue}00 100%)`,
        }}
      />

      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 0.5,
          pt: 2,
          px: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              backgroundColor: blueSoft,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: "completePulse 2s ease-in-out infinite",
            }}
          >
            <TaskAltOutlinedIcon sx={{ color: blue, fontSize: 22 }} />
          </Box>
          <Box
            sx={{
              fontWeight: 700,
              fontSize: { xs: "18px", sm: "20px" },
              color: dt.textPrimary,
              letterSpacing: "-0.3px",
            }}
          >
            {translate("completeWork.title")}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 0.5, pb: 0 }}>
        {/* -- Subtitle -- */}
        <Box
          sx={{
            fontSize: "14px",
            color: dt.textSecondary,
            mb: 3,
            mt: 0.5,
            lineHeight: 1.6,
          }}
        >
          {translate("completeWork.subtitle", { number: offerNumber })}
        </Box>

        {/* -- Completion Summary (required) -- */}
        <Box sx={{ mb: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              mb: 1,
            }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 15, color: dt.textMuted }} />
            <Box
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: dt.textPrimary,
                textTransform: "uppercase",
                letterSpacing: "0.8px",
              }}
            >
              {translate("completeWork.comment")}
              <Box component="span" sx={{ color: "#EF4444", ml: "2px" }}>*</Box>
            </Box>
          </Box>
          <Box
            sx={{
              background: dt.inputBg,
              borderRadius: dt.radiusSm,
              border: summaryError ? "1.5px solid #EF4444" : "1.5px solid transparent",
              padding: "12px",
              transition: "all 0.2s ease",
              "&:focus-within": {
                border: `1.5px solid ${dt.blue}`,
                boxShadow: `0 0 0 3px ${dt.blueGlow}`,
              },
            }}
          >
            <textarea
              value={completionSummary}
              onChange={(e) => {
                setCompletionSummary(e.target.value);
                if (e.target.value.trim()) setSummaryError(false);
              }}
              placeholder={translate("completeWork.commentPlaceholder")}
              rows={4}
              style={{
                border: "none",
                outline: "none",
                width: "100%",
                fontSize: "14px",
                fontFamily: 'var(--font-sans)',
                color: "#1a1a2e",
                background: "transparent",
                resize: "vertical",
                minHeight: "96px",
                boxSizing: "border-box",
              }}
            />
          </Box>
          {summaryError && (
            <Box sx={{ fontSize: "12px", color: "#EF4444", mt: 0.5, fontWeight: 500 }}>
              {translate("execution.summaryRequired")}
            </Box>
          )}
        </Box>

        {/* -- Service Report Upload -- */}
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              mb: 1,
            }}
          >
            <CloudUploadOutlinedIcon sx={{ fontSize: 15, color: dt.textMuted }} />
            <Box
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: dt.textPrimary,
                textTransform: "uppercase",
                letterSpacing: "0.8px",
              }}
            >
              {translate("completeWork.montaznyVykaz")}
            </Box>
          </Box>

          <Box
            sx={{
              fontSize: "12px",
              color: dt.textSecondary,
              mb: 1.5,
              lineHeight: 1.5,
            }}
          >
            {translate("completeWork.uploadHint")}
          </Box>

          {/* Hidden file input (multiple) */}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_FILE_TYPES}
            onChange={handleFileSelect}
            multiple
            style={{ display: "none" }}
          />

          {/* Selected files list */}
          {selectedFiles.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", mb: 1.5 }}>
              {selectedFiles.map((file) => (
                <Box
                  key={file.name}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    border: `1px solid ${dt.cardBorder}`,
                    borderRadius: dt.radiusSm,
                    padding: "10px 14px",
                    backgroundColor: "#F8FAFC",
                  }}
                >
                  <InsertDriveFileOutlinedIcon sx={{ fontSize: 20, color: blue, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: dt.textPrimary,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {file.name}
                    </Box>
                    <Box sx={{ fontSize: "11px", color: dt.textMuted }}>
                      {formatFileSize(file.size)}
                    </Box>
                  </Box>
                  <Box
                    onClick={() => handleRemoveFile(file.name)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      flexShrink: 0,
                      "&:hover": {
                        backgroundColor: "rgba(239,68,68,0.08)",
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16, color: dt.textMuted, "&:hover": { color: "#EF4444" } }} />
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {/* Upload button (always shown, allows adding more files) */}
          <Box
            onClick={() => fileInputRef.current?.click()}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              border: `2px dashed ${dt.cardBorder}`,
              borderRadius: dt.radiusSm,
              padding: selectedFiles.length > 0 ? "10px 16px" : "16px 20px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              backgroundColor: dt.inputBg,
              "&:hover": {
                borderColor: blue,
                backgroundColor: blueSoft,
              },
            }}
          >
            <CloudUploadOutlinedIcon sx={{ fontSize: 20, color: dt.textMuted }} />
            <Box sx={{ fontSize: "13px", fontWeight: 600, color: dt.textSecondary }}>
              {selectedFiles.length > 0
                ? translate("completeWork.addMoreFiles")
                : translate("completeWork.uploadReport")}
            </Box>
          </Box>

          {fileError && (
            <Box sx={{ fontSize: "12px", color: "#EF4444", mt: 0.5, fontWeight: 500 }}>
              {fileError}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 1.5,
          gap: 1,
          flexDirection: isPhone ? "column-reverse" : "row",
          "& > :not(:first-of-type)": {
            ml: isPhone ? "0 !important" : undefined,
          },
        }}
      >
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            color: dt.textSecondary,
            borderRadius: dt.radiusSm,
            px: 2.5,
            py: 1,
            transition: "all 0.2s ease",
            width: isPhone ? "100%" : "auto",
            "&:hover": {
              backgroundColor: dt.inputBg,
              color: dt.textPrimary,
            },
          }}
        >
          {translate("common:buttons.cancel")}
        </Button>
        <Button
          onClick={handleComplete}
          variant="contained"
          disabled={!isValid || loading}
          startIcon={
            loading ? (
              <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
            ) : (
              <TaskAltOutlinedIcon sx={{ fontSize: "18px !important" }} />
            )
          }
          sx={{
            background: `linear-gradient(135deg, ${blue} 0%, #60A5FA 100%)`,
            borderRadius: dt.radiusPill,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            px: 3,
            py: 1,
            boxShadow: "0 2px 8px rgba(59,130,246,0.25)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            width: isPhone ? "100%" : "auto",
            whiteSpace: "nowrap",
            "&:hover": {
              background: `linear-gradient(135deg, #60A5FA 0%, ${blue} 100%)`,
              transform: "translateY(-2px)",
              boxShadow: "0 6px 20px rgba(59,130,246,0.3)",
            },
            "&.Mui-disabled": {
              background: "#D1D5DB",
              color: "#9CA3AF",
              boxShadow: "none",
            },
          }}
        >
          {loading
            ? translate("execution.markingComplete")
            : translate("completeWork.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompleteWorkDialog;
